import { HttpClient, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  catchError,
  exhaustMap,
  filter,
  finalize,
  map,
  takeWhile,
  tap,
  throwError,
  timer,
} from 'rxjs';

import {
  AutomaticProjectSetup,
  EmployeeHours,
  ImportHistory,
  ImportHistorySummaryResponse,
  JoinMasterProjectInfo,
  PaginatedList,
  Plan,
  Project,
  ProjectCopy,
  ProjectDeletionWarnings,
  ProjectIncludeOptions,
  ProjectPlanningUpdate,
  ProjectProgressChartDataResponse,
  ProjectSetupStep,
  ProjectStatus,
  ProjectStorage,
  ProjectSummaryResponse,
  SetupProgress,
  SubProjectInviteToken,
  WorkUnitInTime,
} from '@shared/models';

import { BaseService } from '../base.service';
import { httpParams } from '../http-params.builder';
import { LoadersService } from '../loaders.service';
import { Filter } from '../table-filters.service';

@Injectable({
  providedIn: 'root',
})
export class ProjectService extends BaseService {
  private _projectId$ = new BehaviorSubject<string | undefined>(undefined);

  get projectId$(): Observable<string | undefined> {
    return this._projectId$.asObservable();
  }

  get projectId(): string | undefined {
    return this._projectId$.value;
  }

  constructor(
    private http: HttpClient,
    private loadersService: LoadersService
  ) {
    super();
  }

  setProjectId(projectId: string | undefined): void {
    this._projectId$.next(projectId);
  }

  getAll(
    loader: string | undefined,
    includeRemoved: boolean,
    statuses?: ProjectStatus[],
    ids?: string[]
  ): Observable<Array<Project>> {
    this.loadersService.show(loader);
    return this.http
      .get<Array<Project>>(`${this.baseUrl}/projects`, {
        params: httpParams().set('includeRemoved', includeRemoved).arrays({ statuses, ids }).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getAllWithIncludes(
    loader: string | undefined,
    options?: ProjectIncludeOptions | null,
    pageIndex = 0,
    pageSize = 20,
    filters: Filter = null
  ): Observable<PaginatedList<Project>> {
    this.loadersService.show(loader);
    const params = httpParams().paging(pageIndex, pageSize).json('filters', filters).json('options', options).build();

    return this.http
      .get<PaginatedList<Project>>(`${this.baseUrl}/projects/with-includes`, { params })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getById(loader: string, id: string): Observable<Project> {
    this.loadersService.show(loader);
    return this.http
      .get<Project>(`${this.baseUrl}/projects/${id}`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getRecalculationStatus(projectId: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.baseUrl}/projects/${projectId}/recalculation-status`);
  }

  getAiProcessingStatus(projectId: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.baseUrl}/projects/${projectId}/ai-processing-status`);
  }

  startAutomaticSetup(request: AutomaticProjectSetup): Observable<Project> {
    return this.http.post<Project>(`${this.baseUrl}/projects/automatic-setup`, request);
  }

  getAutomaticSetupProgress(projectId: string): Observable<SetupProgress> {
    return this.http.get<SetupProgress>(`${this.baseUrl}/projects/${projectId}/automatic-setup-progress`);
  }

  pollAutomaticSetupProgress(projectId: string): Observable<SetupProgress> {
    return timer(0, 2000).pipe(
      exhaustMap(() =>
        this.getAutomaticSetupProgress(projectId).pipe(
          catchError((error) => {
            const status = error.statusCode ?? error.status;
            return status === 0 || status === 408 || status === 429 || status >= 500 ? EMPTY : throwError(() => error);
          })
        )
      ),
      takeWhile(
        (progress) =>
          progress.currentStep === ProjectSetupStep.SummarizingFiles ||
          progress.currentStep === ProjectSetupStep.ProcessingProject,
        true
      )
    );
  }

  getByIdWithIncludes(loader: string, id: string, options?: ProjectIncludeOptions | null): Observable<Project> {
    this.loadersService.show(loader);

    return this.http
      .get<Project>(`${this.baseUrl}/projects/${id}/with-includes`, {
        params: httpParams().json('options', options).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getSubProjectInviteToken(loader: string, masterProjectId: string): Observable<SubProjectInviteToken> {
    this.loadersService.show(loader);

    return this.http
      .post<SubProjectInviteToken>(`${this.baseUrl}/projects/${masterProjectId}/master-project/invite-token`, {})
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getJoinMasterProjectInfo(loader: string, masterProjectId: string, token: string): Observable<JoinMasterProjectInfo> {
    this.loadersService.show(loader);

    return this.http
      .get<JoinMasterProjectInfo>(`${this.baseUrl}/projects/${masterProjectId}/master-project/join-info`, {
        params: httpParams().from({ masterProjectId, token }).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  joinMasterProject(loader: string, masterProjectId: string, token: string, projectId: string): Observable<Project> {
    this.loadersService.show(loader);

    return this.http
      .post<Project>(
        `${this.baseUrl}/projects/${masterProjectId}/sub-projects`,
        {},
        {
          params: httpParams().from({ token, subProjectId: projectId }).build(),
        }
      )
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  removeSubProject(loader: string, masterProjectId: string, subProjectId: string): Observable<string> {
    this.loadersService.show(loader);

    return this.http
      .delete<string>(`${this.baseUrl}/projects/${masterProjectId}/sub-projects/${subProjectId}`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  updateSubProjectColor(
    loader: string,
    masterProjectId: string,
    subProjectId: string,
    color: string | null
  ): Observable<string> {
    this.loadersService.show(loader);

    return this.http
      .put<string>(`${this.baseUrl}/projects/${masterProjectId}/sub-projects/${subProjectId}/color`, { color })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  add(loader: string, dto: Project, options?: ProjectIncludeOptions | null): Observable<Project> {
    this.loadersService.show(loader);

    return this.http
      .post<Project>(this.baseUrl + '/projects', dto, {
        params: httpParams().json('options', options).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  edit(
    loader: string,
    dto: { productIdsToDelete?: string[] } & Project,
    options?: ProjectIncludeOptions | null
  ): Observable<Project> {
    this.loadersService.show(loader);

    return this.http
      .put<Project>(this.baseUrl + '/projects', dto, {
        params: httpParams().json('options', options).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  editProgress(loader: string, dto: Project, options?: ProjectIncludeOptions | null): Observable<Project> {
    this.loadersService.show(loader);

    return this.http
      .put<Project>(this.baseUrl + '/projects/progress', dto, {
        params: httpParams().json('options', options).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  editPlannedEmployeesCount(loader: string, dto: Project, options?: ProjectIncludeOptions | null): Observable<Project> {
    this.loadersService.show(loader);

    return this.http
      .put<Project>(this.baseUrl + '/projects/planned-employees-count', dto, {
        params: httpParams().json('options', options).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  editPlanning(loader: string, dto: ProjectPlanningUpdate): Observable<void> {
    this.loadersService.show(loader);

    return this.http
      .put<void>(this.baseUrl + '/projects/planning', dto)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  editAssignedEmployees(
    loader: string,
    projectId: string,
    workUnitsInTime: WorkUnitInTime[],
    assignedEmployeesToRemove: { week: string; userId: string }[],
    options?: ProjectIncludeOptions | null
  ): Observable<Project> {
    this.loadersService.show(loader);

    return this.http
      .put<Project>(
        this.baseUrl + '/projects/assigned-employees',
        {
          id: projectId,
          workUnitsInTime: workUnitsInTime,
          assignedEmployeesToRemove: assignedEmployeesToRemove,
        },
        {
          params: httpParams().json('options', options).build(),
        }
      )
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  editQualityCheckItems(
    loader: string,
    dto: { qualityCheckItemIdsToRemove?: string[] } & Project,
    options?: ProjectIncludeOptions | null
  ): Observable<Project> {
    this.loadersService.show(loader);

    return this.http
      .put<Project>(this.baseUrl + '/projects/quality-check-items', dto, {
        params: httpParams().json('options', options).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  editProducts(
    loader: string,
    dto: { productdsToRemove?: string[] } & Project,
    options?: ProjectIncludeOptions | null
  ): Observable<Project> {
    this.loadersService.show(loader);

    return this.http
      .put<Project>(this.baseUrl + '/projects/products', dto, {
        params: httpParams().json('options', options).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  editPlans(
    loader: string,
    dto: { planIdsToRemove?: string[]; newVersion?: boolean } & Project,
    options?: ProjectIncludeOptions | null
  ): Observable<Project> {
    this.loadersService.show(loader);

    return this.http
      .put<Project>(this.baseUrl + '/projects/plans', dto, {
        params: httpParams().json('options', options).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getPlanById(loader: string, projectId: string, planId: string): Observable<Plan> {
    this.loadersService.show(loader);

    return this.http
      .get<Plan>(`${this.baseUrl}/projects/${projectId}/plans/${planId}`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getPlanVersions(loader: string, projectId: string, planId: string): Observable<Plan[]> {
    this.loadersService.show(loader);

    return this.http
      .get<Plan[]>(`${this.baseUrl}/projects/${projectId}/plans/${planId}/versions`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  changeStatus(loader: string, projectIds: string[], status: ProjectStatus): Observable<string[]> {
    this.loadersService.show(loader);

    const command = {
      status,
      projectIds,
    };
    return this.http
      .put<string[]>(this.baseUrl + '/projects/change-status', command)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  setTimeOfLastCustomerContact(
    loader: string,
    projectId: string,
    options?: ProjectIncludeOptions | null
  ): Observable<Project> {
    this.loadersService.show(loader);

    return this.http
      .put<Project>(
        this.baseUrl + `/projects/${projectId}/time-of-last-customer-contact`,
        {},
        {
          params: httpParams().json('options', options).build(),
        }
      )
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getDeletionWarnings(loader: string, id: string): Observable<ProjectDeletionWarnings> {
    this.loadersService.show(loader);
    return this.http
      .get<ProjectDeletionWarnings>(`${this.baseUrl}/projects/${id}/deletion-warnings`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  delete(loader: string, id: string): Observable<boolean> {
    this.loadersService.show(loader);
    return this.http.delete(`${this.baseUrl}/projects/${id}`, { observe: 'response' }).pipe(
      map((response) => this.isStatusSucceed(response.status)),
      finalize(() => this.loadersService.hide(loader))
    );
  }

  copy(loader: string, model: ProjectCopy, options?: ProjectIncludeOptions | null): Observable<Project> {
    this.loadersService.show(loader);

    return this.http
      .post<Project>(`${this.baseUrl}/projects/copy`, model, {
        params: httpParams().json('options', options).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getProjectStorage(loader: string, projectId: string, key: string): Observable<ProjectStorage | undefined> {
    this.loadersService.show(loader);
    return this.http
      .get<ProjectStorage>(`${this.baseUrl}/projects/${projectId}/storage/${key}`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  setProjectStorage(
    loader: string,
    projectId: string,
    key: string,
    version: string,
    content: string
  ): Observable<ProjectStorage> {
    const request: ProjectStorage = {
      projectId,
      key,
      version,
      content,
    };

    this.loadersService.show(loader);
    return this.http
      .post<ProjectStorage>(`${this.baseUrl}/projects/storage`, request)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getProjectSummary(loader: string, projectId: string): Observable<ProjectSummaryResponse> {
    this.loadersService.show(loader);
    return this.http
      .get<ProjectSummaryResponse>(`${this.baseUrl}/projects/${projectId}/summary`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getProjectProgressHoursChartData(
    loader: string,
    projectId: string,
    workUnitId?: string,
    masterProjectId?: string,
    activityId?: string
  ) {
    this.loadersService.show(loader);

    return this.http
      .get<ProjectProgressChartDataResponse>(`${this.baseUrl}/projects/${projectId}/charts/progress-hours`, {
        params: httpParams().from({ workUnitId, masterProjectId, activityId }).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  importQualityCheckItems(loader: string, projectId: string, file: File): Observable<string[]> {
    this.loadersService.show(loader, 1);
    const formData = new FormData();
    formData.append('', file, file.name);
    const headers = new HttpHeaders({ 'ngsw-bypass': '' });

    return this.http
      .post(`${this.baseUrl}/projects/import/${projectId}`, formData, {
        reportProgress: true,
        observe: 'events',
        headers: headers,
      })
      .pipe(
        tap((event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            this.loadersService.update(loader, Math.round((100 * event.loaded) / event.total));
          }
        }),
        filter((event): event is HttpResponse<string[]> => event.type === HttpEventType.Response),
        map((response) => response.body as string[]),
        finalize(() => this.loadersService.hide(loader))
      );
  }

  exportEmptyQualityCheckItems(loader: string, projectId: string, cultures: string[]) {
    this.loadersService.show(loader);

    const url = `${this.baseUrl}/projects/export/quality-check-items/${projectId}/empty`;
    return this.http
      .get(url, {
        responseType: 'blob',
        params: httpParams().array('cultures', cultures).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  exportQualityCheckItems(loader: string, projectId: string) {
    this.loadersService.show(loader);

    const url = `${this.baseUrl}/projects/export/quality-check-items/${projectId}`;
    return this.http.get(url, { responseType: 'blob' }).pipe(finalize(() => this.loadersService.hide(loader)));
  }

  exportWorkUnits(loader: string, workUnitIds: string[]) {
    this.loadersService.show(loader);

    const url = `${this.baseUrl}/projects/export/work-units`;
    return this.http.post(url, { workUnitIds: workUnitIds }).pipe(finalize(() => this.loadersService.hide(loader)));
  }

  updateEmployeesHours(
    loader: string,
    fromDay: string,
    toDay: string,
    projectId: string | undefined,
    userId: string,
    employeeHours: EmployeeHours[]
  ): Observable<EmployeeHours[]> {
    this.loadersService.show(loader);
    return this.http
      .put<
        EmployeeHours[]
      >(`${this.baseUrl}/projects/employee-hours`, { fromDay, toDay, projectId, userId, employeeHours })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getEmployeeHours(loader: string, filters: Filter = null): Observable<EmployeeHours[]> {
    this.loadersService.show(loader);
    return this.http
      .get<EmployeeHours[]>(`${this.baseUrl}/projects/employee-hours`, {
        params: httpParams().json('filters', filters).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  exportEmployeeHoursToExcel(loader: string, filters: Filter = null) {
    this.loadersService.show(loader);
    const url = `${this.baseUrl}/projects/export/employee-hours/excel`;
    return this.http
      .get(url, { params: httpParams().json('filters', filters).build(), responseType: 'blob' })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  exportEmployeeHoursFinancialExport(loader: string, filters: Filter = null): Observable<HttpResponse<Blob>> {
    this.loadersService.show(loader);
    const url = `${this.baseUrl}/projects/export/employee-hours/financial`;
    return this.http
      .get(url, {
        params: httpParams().json('filters', filters).build(),
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  approveEmployeeHours(loader: string, employeeHoursIds: string[]): Observable<EmployeeHours[]> {
    this.loadersService.show(loader);
    return this.http
      .put<EmployeeHours[]>(`${this.baseUrl}/projects/employee-hours/approve`, { employeeHoursIds: employeeHoursIds })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  rejectEmployeeHours(loader: string, employeeHoursIds: string[]): Observable<EmployeeHours[]> {
    this.loadersService.show(loader);
    return this.http
      .put<EmployeeHours[]>(`${this.baseUrl}/projects/employee-hours/reject`, { employeeHoursIds: employeeHoursIds })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getProductImportHistorySummary(loader: string, projectId: string): Observable<ImportHistorySummaryResponse> {
    this.loadersService.show(loader);
    return this.http
      .get<ImportHistorySummaryResponse>(`${this.baseUrl}/projects/${projectId}/product-import-history`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getProductImportHistoryDetails(loader: string, projectId: string, historyId: string): Observable<ImportHistory> {
    this.loadersService.show(loader);
    return this.http
      .get<ImportHistory>(`${this.baseUrl}/projects/${projectId}/product-import-history/${historyId}`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  updateProductImportHistory(
    loader: string,
    projectId: string,
    importHistories: ImportHistory[],
    productImportOptionsJson?: string
  ): Observable<string> {
    this.loadersService.show(loader);
    return this.http
      .put<string>(`${this.baseUrl}/projects/product-import-history`, {
        projectId: projectId,
        importHistories: importHistories,
        productImportOptionsJson,
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  restore(loader: string, projectId: string): Observable<Project> {
    this.loadersService.show(loader);

    return this.http
      .put<Project>(this.baseUrl + '/projects/restore', { projectId: projectId })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }
}
